// import { z } from 'zod'; // Not needed for client-side route param only
import { TranscriptViewer } from '@/components/calls/TranscriptViewer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TooltipProvider } from '@/components/ui/tooltip';
import { formatDuration, formatTimestampDetailed } from '@/utils/format-call';
import { trpc } from '@/utils/trpc';
import { useSuperAdmin } from '@/utils/use-super-admin';
import { Link, createFileRoute } from '@tanstack/react-router';
import type {
  PhoneCallResponse,
  RouterOutput,
  StructuredTranscriptEntryType,
} from '@trapeze/server';
import {
  ArrowLeft,
  CheckCircle2,
  Copy,
  Download,
  ExternalLink,
  Info,
  Loader2,
  PauseCircle,
  PhoneIncoming,
  PhoneOutgoing,
  PlayCircle,
  XCircle,
} from 'lucide-react';
import * as React from 'react';
// import type { inferRouterOutputs } from '@trpc/client'; // Removed

// --- Custom Types for JSONB fields ---

interface RetellCallAnalysisOutput {
  call_summary?: string | null;
  call_successful?: boolean | null;
  user_sentiment?: string | null;
  in_voicemail?: boolean | null;
  // Any other fields that might be part of this analysis
}
// --- End Custom Types ---

// Types will be inferred from useQuery hook or defined locally if needed for helpers
// Attempt to define StructuredTranscriptEntryType more directly via RouterOutput
type CallDetailFromTRPC = RouterOutput['calls']['getCallDetailById'];

// Augment the TRPC output type with more specific types for jsonb fields
// This helps TypeScript understand the structure of these fields.
// Note: `structuredTranscript` and `retellCallAnalysis` from DB are `unknown` by default for jsonb.
// The actual transformation/parsing from string/Buffer (if that's how jsonb arrives) to these objects
// usually happens in the TRPC client or upon data reception.
// For the purpose of this component, we assume they are already parsed objects.
type CallDetailOutput = Omit<
  CallDetailFromTRPC,
  | 'structuredTranscript'
  | 'retellCallAnalysis'
  | 'callMetadata'
  | 'customAnalysis'
  | 'anpSnapshot'
  | 'appointmentSnapshot'
> & {
  structuredTranscript: StructuredTranscriptEntryType[] | null;
  retellCallAnalysis: RetellCallAnalysisOutput | null;
  callMetadata: Record<string, unknown> | null;
  customAnalysis: Record<string, unknown> | null;
  anpSnapshot: {
    follow_ups: Array<{
      raw: string;
      expanded: string;
      provider?: string;
    }>;
    note_date: string | null;
    cached_at: Date | null;
    snapshotted_at: Date;
  } | null;
  appointmentSnapshot: {
    appointment_ehr_id: string;
    booking_datetime: string;
    provider_id: string;
    provider_name: string;
    facility_id: string;
    facility_address: string;
    appointment_type_ehr_id: string;
    appointment_type_name: string | null;
    slot_notes: string | null;
  } | null;
  topics: string[] | null;
  defaultTags: string[] | null;
  customTags: string[] | null;
  transferDestination: string | null;
  isResolved: boolean | null;
  summary: string | null;
  callerType: string | null;
  callAnalysisStatus: string | null;
  resolutionLabel: string | null;
  bookedAppointmentDatetime: Date | null;
  patient?: { id: string; ehrId: string; firstName: string | null; lastName: string | null } | null;
};

type StructuredTranscriptArray = NonNullable<CallDetailOutput['structuredTranscript']>;

export const Route = createFileRoute('/_protected/calls/$callId')({
  component: CallDetailComponent,
  // No loader or search param validation needed here as per old file structure
  // Params are accessed via useParams()
});

// Enhanced HTML5 Audio Player (Reverted from WaveSurfer)
const AudioPlayer: React.FC<{
  src: string;
  seekToTime?: number | null; // Time in seconds to seek to
  onTimeUpdate?: (currentTime: number, duration: number) => void;
  onPlaybackEnd?: () => void;
  playbackRate?: number;
}> = ({ src, seekToTime, onTimeUpdate, onPlaybackEnd, playbackRate = 1 }) => {
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);

  const formatAudioTime = (timeInSeconds: number): string => {
    if (Number.isNaN(timeInSeconds) || timeInSeconds < 0) return '0:00';
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  React.useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  React.useEffect(() => {
    if (
      audioRef.current &&
      seekToTime !== null &&
      seekToTime !== undefined &&
      Number.isFinite(seekToTime)
    ) {
      // Check if audio metadata is loaded, otherwise seeking might fail or be inaccurate
      if (audioRef.current.readyState >= HTMLMediaElement.HAVE_METADATA) {
        audioRef.current.currentTime = seekToTime;
      } else {
        // If metadata not loaded, wait for it, then seek.
        const handleCanPlay = () => {
          if (audioRef.current) audioRef.current.currentTime = seekToTime;
          audioRef.current?.removeEventListener('canplay', handleCanPlay);
        };
        audioRef.current.addEventListener('canplay', handleCanPlay);
      }
    }
  }, [seekToTime]);

  const handleAudioError = React.useCallback((e: React.SyntheticEvent<HTMLAudioElement, Event>) => {
    console.error('Error with audio element:', e);
    const audioEl = audioRef.current;
    let message = 'Could not play or load audio.';
    if (audioEl?.error) {
      switch (audioEl.error.code) {
        case MediaError.MEDIA_ERR_ABORTED:
          message = 'Audio playback aborted.';
          break;
        case MediaError.MEDIA_ERR_NETWORK:
          message = 'A network error caused audio download to fail.';
          break;
        case MediaError.MEDIA_ERR_DECODE:
          message =
            'Audio playback aborted due to a corruption problem or because the audio used features your browser did not support.';
          break;
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          message =
            'The audio could not be loaded, either because the server or network failed or because the format is not supported.';
          break;
        default:
          message = 'An unknown error occurred with the audio.';
          break;
      }
    }
    setError(message);
    setIsPlaying(false);
  }, []);

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(handleAudioError);
      }
    }
  };

  const handleSeekSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (audioRef.current) {
      const newTime = Number(event.target.value);
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleRestart = React.useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  }, []);

  React.useEffect(() => {
    const audioElement = audioRef.current;
    if (audioElement) {
      const onPlay = () => setIsPlaying(true);
      const onPause = () => setIsPlaying(false);
      const onEnded = () => {
        setIsPlaying(false);
        if (onPlaybackEnd) onPlaybackEnd();
      };
      const onErrorEvent = (e: React.SyntheticEvent<HTMLAudioElement, Event>) =>
        handleAudioError(e);

      const onLoadedMetadata = () => {
        if (audioElement) setDuration(audioElement.duration);
      };
      const onTimeUpdateEvent = () => {
        if (audioElement) {
          const currTime = audioElement.currentTime;
          const dur = audioElement.duration;
          setCurrentTime(currTime);
          if (onTimeUpdate) onTimeUpdate(currTime, dur);
        }
      };

      audioElement.addEventListener('play', onPlay);
      audioElement.addEventListener('pause', onPause);
      audioElement.addEventListener('ended', onEnded);
      audioElement.addEventListener('error', onErrorEvent as unknown as EventListener);
      audioElement.addEventListener('loadedmetadata', onLoadedMetadata);
      audioElement.addEventListener('timeupdate', onTimeUpdateEvent);

      if (audioElement.readyState >= HTMLMediaElement.HAVE_METADATA) {
        setDuration(audioElement.duration);
        setCurrentTime(audioElement.currentTime);
      }

      return () => {
        audioElement.removeEventListener('play', onPlay);
        audioElement.removeEventListener('pause', onPause);
        audioElement.removeEventListener('ended', onEnded);
        audioElement.removeEventListener('error', onErrorEvent as unknown as EventListener);
        audioElement.removeEventListener('loadedmetadata', onLoadedMetadata);
        audioElement.removeEventListener('timeupdate', onTimeUpdateEvent);
      };
    }
  }, [onTimeUpdate, onPlaybackEnd, handleAudioError]);

  if (!src) return <p className="text-muted-foreground text-sm">No recording available.</p>;

  return (
    <div className="space-y-2 py-2">
      {/* Native HTML5 audio element, hidden but used for control */}
      {/* biome-ignore lint/a11y/useMediaCaption: Audio files are call recordings without available captions */}
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        className="hidden"
        onError={handleAudioError}
      >
        {/* Track element removed since we don't have actual captions */}
      </audio>

      {/* Custom Controls */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          onClick={handlePlayPause}
          size="sm"
          variant="outline"
          title={isPlaying ? 'Pause' : 'Play'}
          disabled={!!error}
        >
          {isPlaying ? <PauseCircle className="h-5 w-5" /> : <PlayCircle className="h-5 w-5" />}
        </Button>
        <Button
          onClick={handleRestart}
          size="sm"
          variant="outline"
          title="Restart"
          disabled={!!error}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            role="img"
            aria-label="Restart"
          >
            <title>Restart</title>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 15v-4M8.5 9.5l-3 3 3 3" />
          </svg>
        </Button>
        <div className="text-sm text-muted-foreground min-w-[80px] tabular-nums">
          {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
        </div>
        <input
          type="range"
          min="0"
          max={duration || 0}
          value={currentTime}
          onChange={handleSeekSliderChange}
          className="w-full flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
          disabled={!duration || !!error}
          step="0.1"
        />
        <Button asChild size="sm" variant="outline" title="Download Recording" disabled={!!error}>
          <a href={src} download>
            <Download className="h-4 w-4" />
          </a>
        </Button>
      </div>
      {error && <span className="text-xs text-red-500 py-2 block">Error: {error}</span>}
    </div>
  );
};

// --- SuperAdmin A&P Notes Component ---
const SuperAdminANPCard: React.FC<{
  anpSnapshot: CallDetailOutput['anpSnapshot'];
}> = ({ anpSnapshot }) => {
  const [copiedField, setCopiedField] = React.useState<string | null>(null);

  const handleCopy = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (!anpSnapshot || !anpSnapshot.follow_ups || anpSnapshot.follow_ups.length === 0) {
    return null;
  }

  return (
    <Card className="border-slate-200 dark:border-slate-800">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">Assessment & Plans (A&P) Notes</CardTitle>
        <CardDescription className="text-xs text-muted-foreground mt-1">
          Cached follow-up instructions for verification
          {anpSnapshot.note_date && ` • Note Date: ${anpSnapshot.note_date}`}
          {anpSnapshot.cached_at &&
            ` • Cached: ${new Intl.DateTimeFormat('en-US', {
              dateStyle: 'short',
              timeStyle: 'short',
            }).format(new Date(anpSnapshot.cached_at))}`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {anpSnapshot.follow_ups.map(
          (followUp: { raw: string; expanded: string; provider?: string }, idx: number) => (
            <div
              key={`${followUp.provider || 'unknown'}-${followUp.raw.slice(0, 50)}-${idx}`}
              className="border-l-4 border-slate-300 dark:border-slate-700 pl-3 space-y-2"
            >
              {followUp.provider && (
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Provider: {followUp.provider}
                </div>
              )}

              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Raw
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0"
                    onClick={() => handleCopy(followUp.raw, `raw-${idx}`)}
                    title="Copy raw text"
                  >
                    {copiedField === `raw-${idx}` ? (
                      <CheckCircle2 className="h-3 w-3 text-green-600" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                  </Button>
                </div>
                <div className="bg-slate-50 dark:bg-slate-900 px-3 py-2 rounded text-sm border border-slate-200 dark:border-slate-800">
                  {followUp.raw}
                </div>
              </div>

              {followUp.expanded && followUp.expanded !== followUp.raw && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Expanded
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                      onClick={() => handleCopy(followUp.expanded, `expanded-${idx}`)}
                      title="Copy expanded text"
                    >
                      {copiedField === `expanded-${idx}` ? (
                        <CheckCircle2 className="h-3 w-3 text-green-600" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-900 px-3 py-2 rounded text-sm border border-slate-200 dark:border-slate-800">
                    {followUp.expanded}
                  </div>
                </div>
              )}
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
};

// --- SuperAdmin Debug Component ---
const SuperAdminDebugCard: React.FC<{
  retellCallId: string | null;
  agentId: string | null;
  resolutionLabel: string | null;
  patientEhrId: string | null;
  bookedAppointmentDatetime: Date | null;
}> = ({ retellCallId, agentId, resolutionLabel, patientEhrId, bookedAppointmentDatetime }) => {
  const [copiedField, setCopiedField] = React.useState<string | null>(null);

  const handleCopy = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const retellUrl = retellCallId
    ? `https://dashboard.retellai.com/callHistory?history=${retellCallId}`
    : null;

  const DebugField: React.FC<{
    label: string;
    value: string | null;
    fieldKey: string;
    externalLink?: string;
  }> = ({ label, value, fieldKey, externalLink }) => {
    if (!value) return null;

    return (
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {label}
        </span>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-slate-50 dark:bg-slate-900 px-2 py-1.5 rounded text-xs font-mono break-all border border-slate-200 dark:border-slate-800">
            {value}
          </code>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            onClick={() => handleCopy(value, fieldKey)}
            title="Copy to clipboard"
          >
            {copiedField === fieldKey ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </Button>
          {externalLink && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0"
              asChild
              title="Open in Retell Dashboard"
            >
              <a href={externalLink} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card className="border-slate-200 dark:border-slate-800">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">Debug Information</CardTitle>
        <CardDescription className="text-xs text-muted-foreground mt-1">
          Internal identifiers
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <DebugField
          label="Retell Call ID"
          value={retellCallId}
          fieldKey="retellCallId"
          externalLink={retellUrl || undefined}
        />
        <DebugField label="Agent ID" value={agentId} fieldKey="agentId" />
        <DebugField label="Patient EHR ID" value={patientEhrId} fieldKey="patientEhrId" />
        <DebugField label="Resolution Label" value={resolutionLabel} fieldKey="resolutionLabel" />

        {/* Appointment Booking Status */}
        <div className="flex flex-col gap-1 pt-2 border-t border-slate-200 dark:border-slate-800">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Appointment Booked
          </span>
          <div className="flex items-center gap-2">
            {bookedAppointmentDatetime ? (
              <>
                <code className="flex-1 bg-green-50 dark:bg-green-950/30 px-2 py-1.5 rounded text-xs font-mono border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200">
                  ✓ {formatTimestampDetailed(bookedAppointmentDatetime)}
                </code>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  onClick={() =>
                    handleCopy(
                      formatTimestampDetailed(bookedAppointmentDatetime),
                      'appointmentDatetime'
                    )
                  }
                  title="Copy to clipboard"
                >
                  {copiedField === 'appointmentDatetime' ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                </Button>
              </>
            ) : (
              <code className="flex-1 bg-slate-50 dark:bg-slate-900 px-2 py-1.5 rounded text-xs font-mono border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400">
                ✗ No appointment booked
              </code>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// --- SuperAdmin Appointment Snapshot Component ---
const SuperAdminAppointmentSnapshotCard: React.FC<{
  appointmentSnapshot: CallDetailOutput['appointmentSnapshot'];
  bookedAppointmentDatetime: Date | null;
}> = ({ appointmentSnapshot, bookedAppointmentDatetime }) => {
  const [copied, setCopied] = React.useState(false);

  // Don't show card if no appointment was booked at all
  if (!appointmentSnapshot && !bookedAppointmentDatetime) {
    return null;
  }

  // Legacy support: Show simplified card if we only have booking datetime (old calls)
  if (!appointmentSnapshot && bookedAppointmentDatetime) {
    const legacyBookingDate = new Date(bookedAppointmentDatetime);
    const legacyFormattedTime = !Number.isNaN(legacyBookingDate.getTime())
      ? formatTimestampDetailed(legacyBookingDate)
      : 'N/A';

    return (
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Appointment Booked</CardTitle>
          <CardDescription className="text-xs text-muted-foreground mt-1">
            Captured during call (details not available for older calls)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-green-50 dark:bg-green-950/30 px-3 py-2 rounded border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-2">
              <span className="text-green-800 dark:text-green-200 font-semibold">✓</span>
              <span className="text-sm text-green-800 dark:text-green-200 font-mono">
                {legacyFormattedTime}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Modern format: Full details available
  if (!appointmentSnapshot) {
    return null;
  }

  // Format booking datetime using the same formatter as other timestamps
  // Validate date before formatting to handle potential data corruption
  const bookingDate = appointmentSnapshot.booking_datetime
    ? new Date(appointmentSnapshot.booking_datetime)
    : null;
  const formattedBookingTime =
    bookingDate && !Number.isNaN(bookingDate.getTime())
      ? formatTimestampDetailed(bookingDate)
      : 'N/A';

  // Prepare full copy text
  const allDetailsText = [
    `Booking Time: ${formattedBookingTime}`,
    appointmentSnapshot.appointment_type_name
      ? `Appointment Type: ${appointmentSnapshot.appointment_type_name}`
      : null,
    appointmentSnapshot.provider_name ? `Provider: ${appointmentSnapshot.provider_name}` : null,
    appointmentSnapshot.facility_address
      ? `Facility: ${appointmentSnapshot.facility_address}`
      : null,
    appointmentSnapshot.slot_notes
      ? `Original slot label before booking: ${appointmentSnapshot.slot_notes}`
      : null,
    appointmentSnapshot.appointment_ehr_id
      ? `Appointment ID: ${appointmentSnapshot.appointment_ehr_id}`
      : null,
    appointmentSnapshot.provider_id ? `Provider ID: ${appointmentSnapshot.provider_id}` : null,
    appointmentSnapshot.facility_id ? `Facility ID: ${appointmentSnapshot.facility_id}` : null,
  ]
    .filter(Boolean)
    .join('\n');

  const handleCopyAll = () => {
    navigator.clipboard.writeText(allDetailsText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card className="border-slate-200 dark:border-slate-800">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base font-semibold">Appointment Booked</CardTitle>
            <CardDescription className="text-xs text-muted-foreground mt-1">
              Captured during call
            </CardDescription>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-2"
            onClick={handleCopyAll}
            title="Copy all details"
          >
            {copied ? (
              <>
                <CheckCircle2 className="h-3 w-3 text-green-600" />
                <span className="text-xs">Copied</span>
              </>
            ) : (
              <>
                <Copy className="h-3 w-3" />
                <span className="text-xs">Copy All</span>
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Booking Time - Highlighted */}
        <div className="bg-green-50 dark:bg-green-950/30 px-3 py-2 rounded border border-green-200 dark:border-green-800">
          <div className="flex items-center gap-2">
            <span className="text-green-800 dark:text-green-200 font-semibold">✓</span>
            <span className="text-sm text-green-800 dark:text-green-200 font-mono">
              {formattedBookingTime}
            </span>
          </div>
        </div>

        {/* Two-column grid for details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Appointment Type */}
          {appointmentSnapshot.appointment_type_name && (
            <div className="bg-slate-50 dark:bg-slate-900 px-3 py-2 rounded text-sm border border-slate-200 dark:border-slate-800">
              <span className="text-xs text-muted-foreground block mb-1">Type</span>
              <span className="font-medium">{appointmentSnapshot.appointment_type_name}</span>
            </div>
          )}

          {/* Provider */}
          <div className="bg-slate-50 dark:bg-slate-900 px-3 py-2 rounded text-sm border border-slate-200 dark:border-slate-800">
            <span className="text-xs text-muted-foreground block mb-1">Provider</span>
            <span className="font-medium">
              {appointmentSnapshot.provider_name || (
                <span className="text-muted-foreground italic">Not available</span>
              )}
            </span>
          </div>

          {/* Facility */}
          <div className="bg-slate-50 dark:bg-slate-900 px-3 py-2 rounded text-sm border border-slate-200 dark:border-slate-800 md:col-span-2">
            <span className="text-xs text-muted-foreground block mb-1">Facility</span>
            <span className="text-sm">
              {appointmentSnapshot.facility_address || (
                <span className="text-muted-foreground italic">Not available</span>
              )}
            </span>
          </div>
        </div>

        {/* Original Slot Label (if present) - Keep amber highlighting for importance */}
        {appointmentSnapshot.slot_notes && (
          <div className="bg-amber-50 dark:bg-amber-950/30 px-3 py-2 rounded border border-amber-200 dark:border-amber-800">
            <span className="text-xs text-muted-foreground block mb-1">
              Original slot label before booking
            </span>
            <span className="text-sm text-amber-900 dark:text-amber-100">
              {appointmentSnapshot.slot_notes}
            </span>
          </div>
        )}

        {/* IDs row (only if any IDs are present) */}
        {(appointmentSnapshot.appointment_ehr_id ||
          appointmentSnapshot.provider_id ||
          appointmentSnapshot.facility_id) && (
          <div className="pt-2 border-t border-slate-200 dark:border-slate-800 space-y-1">
            {appointmentSnapshot.appointment_ehr_id && (
              <div className="text-xs">
                <span className="text-muted-foreground">Appt ID: </span>
                <code className="font-mono">{appointmentSnapshot.appointment_ehr_id}</code>
              </div>
            )}
            {appointmentSnapshot.provider_id && (
              <div className="text-xs">
                <span className="text-muted-foreground">Provider ID: </span>
                <code className="font-mono">{appointmentSnapshot.provider_id}</code>
              </div>
            )}
            {appointmentSnapshot.facility_id && (
              <div className="text-xs">
                <span className="text-muted-foreground">Facility ID: </span>
                <code className="font-mono">{appointmentSnapshot.facility_id}</code>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// --- Main Component ---
function CallDetailComponent() {
  const { callId } = Route.useParams();
  const searchState = Route.useSearch();
  const [audioSeekToTime, setAudioSeekToTime] = React.useState<number | null>(null);
  const [currentPlaybackRate, setCurrentPlaybackRate] = React.useState(1);

  const {
    data: callDetailData, // Renamed to avoid conflict with potentially typed callDetail
    isLoading,
    isError,
    error,
  } = trpc.calls.getCallDetailById.useQuery({ callId }, { enabled: !!callId });

  // Cast the data to our augmented type.
  // Add runtime checks if necessary, depending on data guarantees from TRPC.
  const callDetail = callDetailData as CallDetailOutput | undefined;

  // Check if call has been analyzed
  const isAnalyzed = callDetail?.callAnalysisStatus === 'COMPLETED';

  // Check if user is super admin
  const { isSuperAdmin } = useSuperAdmin();

  if (isLoading) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[calc(100vh-10rem)]">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-lg text-muted-foreground">Loading call details...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[calc(100vh-10rem)]">
        <XCircle className="h-12 w-12 text-destructive mb-4" />
        <p className="text-lg text-destructive">Error loading call details</p>
        <p className="text-sm text-muted-foreground">
          {error?.message || 'An unknown error occurred.'}
        </p>
      </div>
    );
  }

  if (!callDetail) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[calc(100vh-10rem)]">
        <Info className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-lg text-muted-foreground">Call details not found.</p>
      </div>
    );
  }

  // Destructure fields from callDetail to use below
  const {
    startTimestamp,
    endTimestamp,
    durationMs,
    direction,
    fromNumber,
    toNumber,
    callStatus,
    recordingUrl,
    transcript,
    structuredTranscript,
    agentVersion,
    patientId,
    disconnectionReason, // Already present
    isResolved,
    transferDestination,
    topics,
    defaultTags,
    customTags,
    summary,
    callerType,
  } = callDetail;

  // Helper from dashboard for transferred status (can be adapted)
  const wasCallTransferred = (detail: CallDetailOutput | undefined): boolean => {
    if (!detail || !detail.structuredTranscript) return false;
    // Ensure structuredTranscript is treated as an array
    const transcriptArray = Array.isArray(detail.structuredTranscript)
      ? detail.structuredTranscript
      : [];
    return transcriptArray.some((entry) => {
      if (entry.role === 'tool_call_invocation') {
        const toolCall = entry as PhoneCallResponse.ToolCallInvocationUtterance;
        return toolCall.name === 'transfer_call';
      }
      return false;
    });
  };
  const isTransferred = wasCallTransferred(callDetail) || !!transferDestination;

  // --- ACTION SUMMARY BANNER ---
  const actionSummaryColor = !isAnalyzed
    ? 'bg-gray-50 border-gray-200'
    : !isResolved
      ? 'bg-yellow-50 border-yellow-200'
      : 'bg-green-50 border-green-200';

  // Helper to render analysis items consistently
  const AnalysisItem: React.FC<{
    label: string;
    value?: React.ReactNode;
    badgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline' | null;
    requiresAnalysis?: boolean;
  }> = ({ label, value, badgeVariant, requiresAnalysis = false }) => {
    if (!isAnalyzed && requiresAnalysis) {
      return (
        <div className="flex justify-between items-center text-sm py-1">
          <span className="text-muted-foreground">{label}:</span>
          <span className="text-right">
            <Badge variant="outline" className="text-xs text-muted-foreground">
              Pending Analysis
            </Badge>
          </span>
        </div>
      );
    }

    if (value === undefined || value === null || value === '') value = 'N/A';

    let valueNode = value;
    if (typeof value === 'boolean') {
      valueNode = (
        <Badge variant={badgeVariant || (value ? 'default' : 'secondary')} className="text-xs">
          {value ? 'Yes' : 'No'}
        </Badge>
      );
    } else if (
      typeof value === 'string' &&
      (badgeVariant || label.toLowerCase().includes('sentiment'))
    ) {
      valueNode = (
        <Badge variant={badgeVariant || 'outline'} className="capitalize text-xs">
          {value}
        </Badge>
      );
    }

    return (
      <div className="flex justify-between items-center text-sm py-1">
        <span className="text-muted-foreground">{label}:</span>
        <span className="text-right">{valueNode}</span>
      </div>
    );
  };

  const handleSeekAudio = (timeInSeconds: number) => {
    setAudioSeekToTime(null); // Reset to allow re-triggering if same time is clicked
    setTimeout(() => setAudioSeekToTime(timeInSeconds), 0);
  };

  const playbackRates = [0.5, 0.75, 1, 1.25, 1.5, 2];

  return (
    <TooltipProvider>
      <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl md:text-3xl font-bold">Call Details</h1>
            {!isAnalyzed && (
              <Badge
                variant="outline"
                className="text-muted-foreground bg-yellow-50 border-yellow-200"
              >
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                Analysis Pending
              </Badge>
            )}
            {isAnalyzed && (
              <Badge variant="outline" className="text-green-700 bg-green-50 border-green-200">
                <CheckCircle2 className="mr-1 h-3 w-3" />
                Analysis Complete
              </Badge>
            )}
          </div>
          <Button variant="outline" asChild>
            <Link to="/calls" search={searchState ?? {}}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Logs
            </Link>
          </Button>
        </div>

        {/* --- Action Summary Banner --- */}
        <div
          className={`rounded-lg border ${actionSummaryColor} px-5 py-3.5 flex flex-col md:flex-row md:items-center md:justify-between gap-3`}
        >
          <div className="flex flex-col gap-2 md:gap-3 md:flex-row md:items-center w-full">
            {/* --- ELEVATED SUMMARY --- */}
            <div className="flex-1">
              <span className="block text-base font-semibold mb-1">
                {!isAnalyzed ? 'Call analysis pending...' : summary || 'No summary available.'}
              </span>
              <div className="flex flex-wrap gap-2 items-center mt-1">
                {!isAnalyzed ? (
                  <Badge variant="outline" className="text-muted-foreground text-xs">
                    Analysis Pending
                  </Badge>
                ) : (
                  <>
                    {isResolved ? (
                      <Badge variant="default" className="text-xs">
                        Resolved
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        Unresolved
                      </Badge>
                    )}
                    {isTransferred && transferDestination && (
                      <Badge variant="outline" className="text-xs">
                        Transferred: {transferDestination}
                      </Badge>
                    )}
                  </>
                )}
                <span className="ml-2 text-xs font-medium">
                  Status:{' '}
                  <Badge variant="outline" className="text-xs">
                    {callStatus || 'N/A'}
                  </Badge>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* --- Super Admin Cards --- */}
        {isSuperAdmin && (
          <>
            <SuperAdminDebugCard
              retellCallId={callDetail.retellCallId ?? null}
              agentId={callDetail.agentId ?? null}
              resolutionLabel={callDetail.resolutionLabel ?? null}
              patientEhrId={callDetail.patient?.ehrId ?? null}
              bookedAppointmentDatetime={callDetail.bookedAppointmentDatetime ?? null}
            />
            <SuperAdminANPCard anpSnapshot={callDetail.anpSnapshot ?? null} />
            <SuperAdminAppointmentSnapshotCard
              appointmentSnapshot={callDetail.appointmentSnapshot ?? null}
              bookedAppointmentDatetime={callDetail.bookedAppointmentDatetime ?? null}
            />
          </>
        )}

        {/* --- Call Outcome & Quick Summary --- */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-xl">Call Overview</CardTitle>
            <CardDescription>Key information and status for this call.</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 text-sm">
            {/* Column 1: Call & Participant Info */}
            <div className="space-y-2">
              <h3 className="font-semibold text-base mb-1">Participants & Routing</h3>
              <AnalysisItem
                label="Direction"
                value={
                  <span className="capitalize flex items-center">
                    {direction === 'inbound' ? (
                      <PhoneIncoming className="inline mr-1 h-3 w-3 text-green-500" />
                    ) : (
                      <PhoneOutgoing className="inline mr-1 h-3 w-3 text-blue-500" />
                    )}
                    {direction}
                  </span>
                }
              />
              <AnalysisItem label="From" value={fromNumber} />
              <AnalysisItem label="To" value={toNumber || 'N/A'} />
            </div>

            {/* Column 2: Timings & Agent */}
            <div className="space-y-2">
              <h3 className="font-semibold text-base mb-1">Timings & Agent</h3>
              <AnalysisItem label="Start Time" value={formatTimestampDetailed(startTimestamp)} />
              <AnalysisItem label="End Time" value={formatTimestampDetailed(endTimestamp)} />
              <AnalysisItem label="Duration" value={formatDuration(durationMs)} />

              {agentVersion && <AnalysisItem label="Agent Version" value={`v${agentVersion}`} />}
              {patientId && (
                <AnalysisItem
                  label="Patient ID"
                  value={
                    <span className="font-mono text-xs truncate max-w-[150px] sm:max-w-[200px] inline-block">
                      {patientId}
                    </span>
                  }
                />
              )}
            </div>

            {/* Column 3: Outcome & Details */}
            <div className="space-y-2">
              <h3 className="font-semibold text-base mb-1">Outcome & Details</h3>
              <AnalysisItem label="Transferred" value={isTransferred} />
              <AnalysisItem
                label="Escalated"
                value={false}
                badgeVariant={'outline'}
                requiresAnalysis={true}
              />
              <AnalysisItem
                label="Resolved"
                value={isResolved}
                badgeVariant={isResolved ? 'default' : 'secondary'}
                requiresAnalysis={true}
              />
              {disconnectionReason && (
                <AnalysisItem label="Disconnect Reason" value={disconnectionReason} />
              )}
              <AnalysisItem
                label="Caller Type"
                value={callerType}
                badgeVariant="outline"
                requiresAnalysis={true}
              />
              {isAnalyzed && topics && topics.length > 0 && (
                <div className="mt-2">
                  <span className="text-sm text-muted-foreground block mb-1">Topics:</span>
                  <div className="flex flex-wrap gap-1">
                    {topics.map((topic) => (
                      <Badge key={topic} variant="secondary" className="text-xs">
                        {topic}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {!isAnalyzed && (
                <div className="mt-2">
                  <span className="text-sm text-muted-foreground block mb-1">Topics:</span>
                  <Badge variant="outline" className="text-xs text-muted-foreground">
                    Pending Analysis
                  </Badge>
                </div>
              )}
              {isAnalyzed && defaultTags && defaultTags.length > 0 && (
                <div className="mt-2">
                  <span className="text-sm text-muted-foreground block mb-1">Default Tags:</span>
                  <div className="flex flex-wrap gap-1">
                    {defaultTags.map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {!isAnalyzed && (
                <div className="mt-2">
                  <span className="text-sm text-muted-foreground block mb-1">Default Tags:</span>
                  <Badge variant="outline" className="text-xs text-muted-foreground">
                    Pending Analysis
                  </Badge>
                </div>
              )}
              {isAnalyzed && customTags && customTags.length > 0 && (
                <div className="mt-2">
                  <span className="text-sm text-muted-foreground block mb-1">Custom Tags:</span>
                  <div className="flex flex-wrap gap-1">
                    {customTags.map((tag) => (
                      <Badge
                        key={tag}
                        variant="outline"
                        className="text-xs bg-blue-50 dark:bg-blue-900/30"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {!isAnalyzed && (
                <div className="mt-2">
                  <span className="text-sm text-muted-foreground block mb-1">Custom Tags:</span>
                  <Badge variant="outline" className="text-xs text-muted-foreground">
                    Pending Analysis
                  </Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* --- Transcript Section --- */}
        <Card>
          <CardHeader>
            <CardTitle>Transcript</CardTitle>
            {recordingUrl && (
              <div className="mt-4 space-y-3">
                <AudioPlayer
                  src={recordingUrl}
                  seekToTime={audioSeekToTime}
                  playbackRate={currentPlaybackRate}
                />
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Speed:</span>
                  {playbackRates.map((rate) => (
                    <Button
                      key={rate}
                      variant={currentPlaybackRate === rate ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCurrentPlaybackRate(rate)}
                      className="text-xs px-2 py-1 h-auto"
                    >
                      {rate}x
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[600px] p-1">
              {structuredTranscript && structuredTranscript.length > 0 ? (
                <TranscriptViewer transcript={structuredTranscript} isSuperAdmin={isSuperAdmin} />
              ) : transcript ? (
                <pre className="whitespace-pre-wrap text-sm p-3">{transcript}</pre>
              ) : (
                <p className="text-muted-foreground p-4">No transcript available for this call.</p>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
}
